/**
 * Main Web Bundler entrypoint.
 *
 * Qute's {#bundle /} includes this file, so Alpine components used by shared
 * layouts and SSR pages must be registered here.
 */

import htmx from 'htmx.org';
import './messaging-realtime.js';

function configureHtmx() {
    window.htmx = htmx;

    document.addEventListener('htmx:configRequest', (event) => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) {
            event.detail.headers['X-CSRF-Token'] = csrfToken;
        }
    });

    installStyledConfirm();
    installHtmxErrorFeedback();
}

/**
 * Last-resort feedback for failed HTMX requests.
 *
 * htmx does not swap non-2xx responses, so when a server-side error mapper does not
 * render a fragment the user sees nothing at all — the page simply stops responding.
 * This surfaces a toast for network failures and 4xx/5xx responses.
 *
 * Opt out per element with data-suppress-error-toast when the request handles its own
 * error rendering (e.g. an hx-target that receives an error partial).
 */
function installHtmxErrorFeedback() {
    const TOAST_TIMEOUT_MS = 6000;

    function container() {
        let el = document.querySelector('.toast-container');
        if (!el) {
            el = document.createElement('div');
            el.className = 'toast-container';
            el.setAttribute('role', 'status');
            el.setAttribute('aria-live', 'polite');
            document.body.appendChild(el);
        }
        return el;
    }

    function showErrorToast(message) {
        const toast = document.createElement('div');
        toast.className = 'toast toast-error';
        toast.textContent = message;
        container().appendChild(toast);

        requestAnimationFrame(() => toast.classList.add('toast-show'));
        setTimeout(() => {
            toast.classList.remove('toast-show');
            toast.classList.add('toast-hide');
            setTimeout(() => toast.remove(), 300);
        }, TOAST_TIMEOUT_MS);
    }

    function suppressed(elt) {
        return !!elt?.closest?.('[data-suppress-error-toast]');
    }

    document.addEventListener('htmx:responseError', (event) => {
        if (suppressed(event.detail.elt)) {
            return;
        }
        const status = event.detail.xhr?.status;
        showErrorToast(
            status >= 500
                ? 'Something went wrong on our side. Please try again.'
                : 'That action could not be completed. Please reload the page and try again.',
        );
    });

    document.addEventListener('htmx:sendError', (event) => {
        if (suppressed(event.detail.elt)) {
            return;
        }
        showErrorToast('Connection lost. Check your network and try again.');
    });

    document.addEventListener('htmx:timeout', (event) => {
        if (suppressed(event.detail.elt)) {
            return;
        }
        showErrorToast('The request took too long. Please try again.');
    });
}

/**
 * Replaces HTMX's native window.confirm() (triggered by hx-confirm) with the app's
 * styled .confirm-dialog component, so confirmations match the product design instead
 * of the OS-localized browser prompt.
 *
 * One shared <dialog> is reused for every confirmation. The triggering element can tune
 * the dialog via data attributes:
 *   data-confirm-variant="danger|success"  (otherwise inferred from destructive button classes)
 *   data-confirm-title="..."               (heading; defaults to "Are you sure?")
 *   data-confirm-ok="..."                  (accept label; defaults to "Confirm")
 *   data-confirm-cancel="..."              (cancel label; defaults to "Cancel")
 */
function installStyledConfirm() {
    // Inner geometry is intentionally strokeless: .confirm-dialog-icon svg (and its
    // .danger/.success overrides) set the stroke color, so the icon follows the variant.
    const ALERT_ICON =
        '<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
        + '<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>'
        + '<line x1="12" y1="9" x2="12" y2="13"/>'
        + '<line x1="12" y1="17" x2="12.01" y2="17"/>'
        + '</svg>';
    const CHECK_ICON =
        '<svg viewBox="0 0 24 24" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
        + '<polyline points="20 6 9 17 4 12"/>'
        + '</svg>';

    const DESTRUCTIVE_CLASS = /\b(btn-reject|btn-decline|btn-danger|btn-danger-sm|mobile-btn-danger)\b/;

    let dialog = null;

    function ensureDialog() {
        if (dialog) {
            return dialog;
        }
        dialog = document.createElement('dialog');
        dialog.className = 'confirm-dialog';
        dialog.setAttribute('aria-labelledby', 'styled-confirm-title');
        dialog.setAttribute('aria-describedby', 'styled-confirm-message');
        dialog.innerHTML =
            '<div class="confirm-dialog-content">'
            + '<div class="confirm-dialog-icon" aria-hidden="true" data-confirm-icon></div>'
            + '<h2 class="confirm-dialog-title" id="styled-confirm-title" data-confirm-title></h2>'
            + '<p class="confirm-dialog-message" id="styled-confirm-message" data-confirm-message></p>'
            + '<div class="confirm-dialog-actions">'
            + '<button type="button" class="btn btn-secondary" data-confirm-cancel></button>'
            + '<button type="button" class="btn" data-confirm-accept></button>'
            + '</div>'
            + '</div>';
        document.body.appendChild(dialog);
        return dialog;
    }

    function resolveVariant(elt) {
        const explicit = elt?.getAttribute?.('data-confirm-variant');
        if (explicit === 'danger' || explicit === 'success' || explicit === 'warning') {
            return explicit;
        }
        if (elt && DESTRUCTIVE_CLASS.test(elt.className || '')) {
            return 'danger';
        }
        return 'warning';
    }

    document.addEventListener('htmx:confirm', (event) => {
        // htmx resolves hx-confirm from the boosted form; for multi-action forms the
        // confirm text lives on the submitter button instead.
        const submitter = event.detail.triggeringEvent?.submitter ?? null;
        const question = event.detail.question || submitter?.getAttribute?.('hx-confirm');
        // htmx fires this for every request; only intervene when hx-confirm provided text.
        if (!question) {
            return;
        }

        event.preventDefault();

        const elt = submitter || event.detail.elt;
        const variant = resolveVariant(elt);
        const dlg = ensureDialog();

        dlg.classList.remove('danger', 'success');
        if (variant === 'danger' || variant === 'success') {
            dlg.classList.add(variant);
        }

        dlg.querySelector('[data-confirm-icon]').innerHTML = variant === 'success' ? CHECK_ICON : ALERT_ICON;
        dlg.querySelector('[data-confirm-title]').textContent =
            elt?.getAttribute?.('data-confirm-title') || 'Are you sure?';
        dlg.querySelector('[data-confirm-message]').textContent = question;

        const cancelBtn = dlg.querySelector('[data-confirm-cancel]');
        const acceptBtn = dlg.querySelector('[data-confirm-accept]');
        cancelBtn.textContent = elt?.getAttribute?.('data-confirm-cancel') || 'Cancel';
        acceptBtn.textContent = elt?.getAttribute?.('data-confirm-ok') || 'Confirm';
        acceptBtn.className = variant === 'danger' ? 'btn btn-danger' : 'btn btn-primary';

        const previousFocus = document.activeElement;
        let settled = false;

        const close = (accepted) => {
            if (settled) {
                return;
            }
            settled = true;
            cancelBtn.removeEventListener('click', onCancel);
            acceptBtn.removeEventListener('click', onAccept);
            dlg.removeEventListener('cancel', onDialogCancel);
            dlg.removeEventListener('click', onBackdrop);
            dlg.close();
            if (previousFocus && typeof previousFocus.focus === 'function') {
                previousFocus.focus();
            }
            if (accepted) {
                // Skip re-confirmation so htmx issues the original request immediately.
                event.detail.issueRequest(true);
            }
        };

        const onCancel = () => close(false);
        const onAccept = () => close(true);
        const onDialogCancel = (e) => {
            // Esc key — handle ourselves so focus restore + listener cleanup still run.
            e.preventDefault();
            close(false);
        };
        const onBackdrop = (e) => {
            if (e.target === dlg) {
                close(false);
            }
        };

        cancelBtn.addEventListener('click', onCancel);
        acceptBtn.addEventListener('click', onAccept);
        dlg.addEventListener('cancel', onDialogCancel);
        dlg.addEventListener('click', onBackdrop);

        dlg.showModal();
        acceptBtn.focus();
    });
}

function timeOfDayLabel(hour) {
    if (hour >= 5 && hour < 12) {
        return 'morning';
    }
    if (hour >= 12 && hour < 17) {
        return 'afternoon';
    }
    return 'evening';
}

function localGreeting() {
    return {
        greeting: '',

        init() {
            const userName = this.$el?.dataset?.userName?.trim() || 'there';
            this.greeting = `Good ${timeOfDayLabel(new Date().getHours())}, ${userName}!`;
        }
    };
}

function pickTimePicker() {
    return {
        selectedSlot: null,
        showDecline: false,

        init() {
            const checked = this.$el.querySelector('input[name="slotId"]:checked');
            this.selectedSlot = checked ? checked.value : null;
        },

        toggleDecline() {
            this.showDecline = !this.showDecline;
        },

        closeDecline() {
            this.showDecline = false;
        }
    };
}

function dropdown() {
    return {
        open: false,

        toggle() {
            this.open = !this.open;
        },

        close() {
            this.open = false;
        }
    };
}

/**
 * Portal header drawer. Expects x-refs toggle, overlay and drawer; the overlay ships with a
 * static `inert` so it stays out of the tab order and accessibility tree before Alpine boots.
 */
function mobileNav() {
    const LOCK_CLASS = 'mobile-nav-open';

    return {
        open: false,

        init() {
            document.documentElement.classList.remove(LOCK_CLASS);
            this.$refs.drawer.querySelectorAll('.mobile-nav-link.active')
                .forEach(link => link.setAttribute('aria-current', 'page'));
        },

        toggle() {
            this.open ? this.close() : this.show();
        },

        show() {
            this.open = true;
            this.$refs.overlay.inert = false;
            document.documentElement.classList.add(LOCK_CLASS);
            this.$nextTick(() => this.focusables()[0]?.focus());
        },

        close(returnFocus = true) {
            if (!this.open) return;
            this.open = false;
            this.$refs.overlay.inert = true;
            document.documentElement.classList.remove(LOCK_CLASS);
            if (returnFocus) this.$refs.toggle.focus();
        },

        closeIfDesktop() {
            if (getComputedStyle(this.$refs.toggle).display === 'none') this.close(false);
        },

        onLinkClick(event) {
            const opensElsewhere = event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0;
            if (event.target.closest('a') && !opensElsewhere) this.close(false);
        },

        focusables() {
            return [...this.$refs.drawer.querySelectorAll('a[href], button:not([disabled])')];
        },

        trapFocus(event) {
            const items = this.focusables();
            if (items.length === 0) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    };
}

function timezoneDetect(fallbackName = 'Local timezone', fallbackOffset = '') {
    return {
        displayName: fallbackName || 'Local timezone',
        offset: fallbackOffset || '',
        currentTz: fallbackName || '',
        showPicker: false,
        commonTimezones: [
            { id: 'UTC', label: 'UTC' },
            { id: 'Europe/London', label: 'London' },
            { id: 'Europe/Berlin', label: 'Berlin' },
            { id: 'Europe/Moscow', label: 'Moscow' },
            { id: 'Asia/Dubai', label: 'Dubai' },
            { id: 'Asia/Tbilisi', label: 'Tbilisi' },
            { id: 'Asia/Almaty', label: 'Almaty' },
            { id: 'Asia/Singapore', label: 'Singapore' },
            { id: 'America/New_York', label: 'New York' },
            { id: 'America/Los_Angeles', label: 'Los Angeles' }
        ],

        init() {
            if (typeof Intl === 'undefined') return;

            const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (detected) {
                this.currentTz = detected;
                this.displayName = detected;
            }
            this.offset = this.offsetFor(this.currentTz) || this.offset;
        },

        selectTimezone(timezone) {
            if (!timezone) return;

            this.currentTz = timezone;
            this.displayName = timezone;
            this.offset = this.offsetFor(timezone) || this.offset;
            this.showPicker = false;
        },

        offsetFor(timezone) {
            if (!timezone || typeof Intl === 'undefined') return '';

            try {
                const parts = new Intl.DateTimeFormat('en-US', {
                    timeZone: timezone,
                    timeZoneName: 'shortOffset'
                }).formatToParts(new Date());
                const value = parts.find(part => part.type === 'timeZoneName')?.value;
                return value ? value.replace('GMT', 'UTC') : '';
            } catch (error) {
                return '';
            }
        }
    };
}

function schedulingForm() {
    return {
        selectedDuration: 60,
        slotCount: 2,
        slot1Date: '',
        slot1Time: '10:00',
        slot2Date: '',
        slot2Time: '14:00',
        slot3Date: '',
        slot3Time: '11:00',
        tomorrowDate: '',
        validationError: '',
        validationWarning: '',

        init() {
            this.tomorrowDate = this.formatDate(this.addDays(new Date(), 1));

            const checked = this.$el.querySelector('input[name="duration"]:checked');
            if (checked) {
                this.selectedDuration = Number.parseInt(checked.value, 10) || 60;
            }
        },

        removeSlot3() {
            this.slotCount = 2;
            this.slot3Date = '';
            this.slot3Time = '11:00';
            this.validationError = '';
            this.validationWarning = '';
        },

        validateAndSubmit(form) {
            this.validationError = '';
            this.validationWarning = '';

            const slots = [
                { label: 'Slot 1', date: this.slot1Date, time: this.slot1Time },
                { label: 'Slot 2', date: this.slot2Date, time: this.slot2Time }
            ];

            if (this.slotCount >= 3) {
                slots.push({ label: 'Slot 3', date: this.slot3Date, time: this.slot3Time });
            }

            for (const slot of slots) {
                if (!slot.date || !slot.time) {
                    this.validationError = `${slot.label} needs both a date and time.`;
                    return;
                }

                if (slot.date < this.tomorrowDate) {
                    this.validationError = `${slot.label} must be at least tomorrow.`;
                    return;
                }
            }

            const seen = new Set();
            for (const slot of slots) {
                const key = `${slot.date}T${slot.time}`;
                if (seen.has(key)) {
                    this.validationError = 'Please propose unique time slots.';
                    return;
                }
                seen.add(key);
            }

            form.submit();
        },

        addDays(date, days) {
            const next = new Date(date);
            next.setDate(next.getDate() + days);
            return next;
        },

        formatDate(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    };
}

/**
 * Phone field: country select plus an auto-prefixed national-digits input.
 *
 * The two inputs are ordinary named form fields, so the field still submits with scripting
 * disabled; the server combines them into E.164. This component only filters the digits,
 * keeps the visible "+CC" prefix in sync, and re-homes the country when someone types or
 * pastes a full international number.
 *
 * Trunk prefixes are deliberately left alone: a leading zero is a dialling artefact in
 * Germany but part of the number in Italy, and only libphonenumber knows the difference.
 */
function phoneInput() {
    return {
        region: '',
        national: '',

        init() {
            const el = this.$el;
            this.region = el.dataset.initialRegion || 'US';
            this.national = el.dataset.initialNational || '';
        },

        get prefix() {
            const dial = this.dialCodeFor(this.region);
            return dial ? `+${dial}` : '+';
        },

        get placeholder() {
            const option = this.optionFor(this.region);
            return option && option.dataset.example ? option.dataset.example : 'Phone number';
        },

        get maxDigits() {
            const option = this.optionFor(this.region);
            const max = option ? Number(option.dataset.max) : NaN;
            return Number.isFinite(max) && max > 0 ? max : 15;
        },

        onInput() {
            const rehomed = this.rehomeInternational(this.national);
            if (rehomed) {
                this.region = rehomed.region;
                this.national = rehomed.national;
                return;
            }
            this.national = this.national.replace(/\D/g, '').slice(0, this.maxDigits);
        },

        // Typing "+1" into a field that already shows a prefix used to produce an unsaveable
        // number. Now the country select follows the typed code and the field keeps the rest.
        rehomeInternational(raw) {
            const trimmed = (raw || '').trim();
            let digits;
            if (trimmed.startsWith('+')) {
                digits = trimmed.slice(1).replace(/\D/g, '');
            } else if (trimmed.startsWith('00')) {
                digits = trimmed.replace(/\D/g, '').slice(2);
            } else {
                return null;
            }
            if (!digits) {
                return null;
            }
            const match = this.matchDialCode(digits);
            return match ? { region: match.region, national: digits.slice(match.dial.length) } : null;
        },

        // Longest match wins: +1 and +1242 share a first digit.
        matchDialCode(digits) {
            const select = this.$refs.country;
            if (!select) {
                return null;
            }
            let best = null;
            for (const option of select.options) {
                const dial = option.dataset.dial || '';
                if (dial && digits.startsWith(dial) && (!best || dial.length > best.dial.length)) {
                    best = { dial, region: option.value };
                }
            }
            return best;
        },

        optionFor(region) {
            const select = this.$refs.country;
            return select ? select.querySelector(`option[value="${region}"]`) : null;
        },

        dialCodeFor(region) {
            const option = this.optionFor(region);
            return option ? option.dataset.dial || '' : '';
        }
    };
}

function locationAutocomplete() {
    return {
        query: '',
        country: '',
        region: '',
        place: '',
        latitude: '',
        longitude: '',
        placeId: '',
        mode: 'full',
        loading: false,
        showSuggestions: false,
        suggestions: [],
        selectedIndex: -1,
        debounceTimer: null,
        abortController: null,
        // True once a location has been committed (picked from the dropdown or restored from
        // the URL). In search mode this swaps the long, clipped text input for a compact,
        // fully-readable removable token. Free typing resets it back to the editable input.
        selected: false,

        init() {
            const el = this.$el;
            this.country = el.dataset.initialCountry || '';
            this.region = el.dataset.initialRegion || '';
            this.place = el.dataset.initialPlace || '';
            this.latitude = el.dataset.initialLatitude || '';
            this.longitude = el.dataset.initialLongitude || '';
            this.placeId = el.dataset.initialPlaceId || '';
            this.mode = el.dataset.mode || 'full';
            this.query = el.dataset.initialQuery || this.formatLocationLabel();
            this.selected = !!(this.place || this.region || this.country);
        },

        formatLocationLabel() {
            return [this.place, this.region, this.country]
                .filter(Boolean)
                .join(', ');
        },

        // Concise, human-readable label for the committed-location token. Keeps the most
        // useful parts (city + region or country) without the long Mapbox tail that gets
        // clipped inside the narrow filters column.
        chipLabel() {
            const primary = this.place || this.region || this.country;
            const secondary = this.place ? (this.region || this.country) : (this.place ? this.region : '');
            return [primary, primary === secondary ? '' : secondary].filter(Boolean).join(', ');
        },

        mapboxToken() {
            const meta = document.querySelector('meta[name="mapbox-token"]');
            const token = meta?.getAttribute('content') || '';
            return token && token !== 'not-configured' ? token : '';
        },

        onInput() {
            const value = this.query.trim();
            this.selectedIndex = -1;
            this.selected = false;
            this.place = value;
            this.placeId = '';
            this.latitude = '';
            this.longitude = '';
            if (!value) {
                // An emptied field means no location; stale hidden parts would still post.
                this.country = '';
                this.region = '';
            }
            this.$dispatch('posting-location-changed');

            clearTimeout(this.debounceTimer);
            if (value.length < 2 || !this.mapboxToken()) {
                this.suggestions = [];
                this.showSuggestions = false;
                this.loading = false;
                return;
            }

            this.debounceTimer = setTimeout(() => this.fetchSuggestions(value), 250);
        },

        async fetchSuggestions(value) {
            if (this.abortController) {
                this.abortController.abort();
            }

            this.abortController = new AbortController();
            this.loading = true;

            const params = new URLSearchParams({
                q: value,
                access_token: this.mapboxToken(),
                autocomplete: 'true',
                limit: '6',
                types: this.mode === 'search' ? 'country,region,place,locality' : 'country,region,place,locality,address'
            });

            try {
                const response = await fetch(
                    'https://api.mapbox.com/search/geocode/v6/forward?' + params.toString(),
                    { signal: this.abortController.signal }
                );
                if (!response.ok) {
                    throw new Error('Mapbox request failed');
                }

                const payload = await response.json();
                this.suggestions = (payload.features || []).map((feature) => this.toSuggestion(feature));
                this.showSuggestions = this.suggestions.length > 0;
            } catch (error) {
                if (error.name !== 'AbortError') {
                    this.suggestions = [];
                    this.showSuggestions = false;
                }
            } finally {
                this.loading = false;
            }
        },

        toSuggestion(feature) {
            const props = feature.properties || {};
            const context = props.context || {};
            const coordinates = props.coordinates || {};
            const geometryCoordinates = feature.geometry?.coordinates || [];
            const featureType = props.feature_type || '';
            // `name` is whichever alias the query matched ("United States of America" for "United");
            // postings store the canonical name, which `name_preferred` and the context entry carry.
            const featureName = props.name_preferred || props.name || '';
            let country = context.country?.name || '';
            let region = context.region?.name || '';
            let place = context.place?.name || context.locality?.name || props.name || '';

            if (featureType === 'country') {
                country = country || featureName;
                region = '';
                place = '';
            } else if (featureType === 'region') {
                region = featureName || region;
                place = '';
            }

            return {
                mapboxId: props.mapbox_id || feature.id || props.full_address || props.name,
                name: featureName || props.full_address || '',
                placeFormatted: props.place_formatted || '',
                country: country,
                region: region,
                place: place,
                latitude: coordinates.latitude ?? geometryCoordinates[1] ?? '',
                longitude: coordinates.longitude ?? geometryCoordinates[0] ?? ''
            };
        },

        selectSuggestion(suggestion) {
            this.country = suggestion.country || '';
            this.region = suggestion.region || '';
            // Search filters match on place alone, so a country or region pick must travel there; an
            // address form must not claim a city it was never given.
            this.place = suggestion.place
                || (this.mode === 'search' ? (suggestion.region || suggestion.country || suggestion.name) : '')
                || '';
            this.latitude = suggestion.latitude || '';
            this.longitude = suggestion.longitude || '';
            this.placeId = suggestion.mapboxId || '';
            this.query = [suggestion.name, suggestion.placeFormatted].filter(Boolean).join(', ');
            this.suggestions = [];
            this.showSuggestions = false;
            this.selectedIndex = -1;
            this.selected = true;
            this.$dispatch('posting-location-changed');
        },

        clearLocation() {
            this.query = '';
            this.country = '';
            this.region = '';
            this.place = '';
            this.latitude = '';
            this.longitude = '';
            this.placeId = '';
            this.suggestions = [];
            this.showSuggestions = false;
            this.selectedIndex = -1;
            this.selected = false;
            this.$dispatch('posting-location-changed');
        },

        // Re-open the editable input for a committed location (search mode token) and place
        // the caret at the end so the user can refine the existing value.
        editLocation() {
            this.selected = false;
            this.$nextTick(() => {
                const input = this.$refs?.input;
                if (input) {
                    input.focus();
                    const len = input.value.length;
                    try { input.setSelectionRange(len, len); } catch (e) { /* no-op */ }
                }
            });
        },

        onBlur() {
            setTimeout(() => {
                this.showSuggestions = false;
            }, 150);
        },

        onKeydown(event) {
            if (!this.showSuggestions || this.suggestions.length === 0) {
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.selectedIndex = (this.selectedIndex + 1) % this.suggestions.length;
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.selectedIndex = this.selectedIndex <= 0
                    ? this.suggestions.length - 1
                    : this.selectedIndex - 1;
            } else if (event.key === 'Enter' && this.selectedIndex >= 0) {
                event.preventDefault();
                this.selectSuggestion(this.suggestions[this.selectedIndex]);
            } else if (event.key === 'Escape') {
                this.showSuggestions = false;
                this.selectedIndex = -1;
            }
        }
    };
}

/**
 * Client for the skill catalogue autocomplete (`taxonomy.skill_catalog`).
 *
 * Forms used to ship their own arrays of skill names, so every form taught a vocabulary the
 * catalogue did not know and the four copies drifted apart. Suggestions now come from
 * GET /api/v1/skills/suggest, ordered by how often the term actually occurs in the corpus.
 *
 * Suggestions are an aid, never a precondition: any failure yields an empty list and the form
 * stays usable with free text.
 */
const skillCatalog = (function createSkillCatalog() {
    const CACHE_LIMIT = 100;
    const cache = new Map();
    let pendingSuggest = null;

    async function load(url, abortable) {
        const cached = cache.get(url);
        if (cached) {
            return cached;
        }
        if (abortable && pendingSuggest) {
            pendingSuggest.abort();
        }
        const controller = abortable ? new AbortController() : null;
        if (abortable) {
            pendingSuggest = controller;
        }
        try {
            const response = await fetch(url, {
                signal: controller?.signal,
                headers: { Accept: 'application/json' }
            });
            if (!response.ok) {
                return [];
            }
            const payload = await response.json();
            const entries = Array.isArray(payload) ? payload : [];
            if (cache.size >= CACHE_LIMIT) {
                cache.clear();
            }
            cache.set(url, entries);
            return entries;
        } catch (error) {
            return [];
        }
    }

    return {
        /** @returns {Promise<Array<{key: string, label: string}>>} */
        suggest(query, limit = 8) {
            const term = (query || '').trim();
            if (term === '') {
                return Promise.resolve([]);
            }
            const url = '/api/v1/skills/suggest?q=' + encodeURIComponent(term) + '&limit=' + limit;
            return load(url, true);
        },

        /** Most used skills, for an empty input or a chip row. */
        top(limit = 8) {
            return load('/api/v1/skills/top?limit=' + limit, false);
        },

        labels(entries) {
            return entries.map((entry) => entry.label);
        }
    };
})();

window.skillCatalog = skillCatalog;

/**
 * Country names from the same Mapbox vocabulary every location field on the site already uses.
 *
 * Work authorisations are matched against the country stored on a job posting by name, so the two
 * sides have to speak one language: a candidate who types "USA" where the posting says "United
 * States" would read as unauthorised and quietly disappear from that country's roles. Suggesting
 * from the same source Mapbox gave the posting removes the class of mismatch rather than
 * apologising for it.
 *
 * Failure yields an empty list, as with skills: an outage must not block a profile from saving.
 */
const countryCatalog = (function createCountryCatalog() {
    const cache = new Map();
    let pending = null;

    function token() {
        const meta = document.querySelector('meta[name="mapbox-token"]');
        const value = meta?.getAttribute('content') || '';
        return value && value !== 'not-configured' ? value : '';
    }

    return {
        async suggest(query, limit = 6) {
            const term = (query || '').trim();
            const accessToken = token();
            if (term.length < 2 || !accessToken) {
                return [];
            }
            if (cache.has(term)) {
                return cache.get(term);
            }
            pending?.abort();
            pending = new AbortController();
            try {
                const url = 'https://api.mapbox.com/search/geocode/v6/forward'
                    + '?q=' + encodeURIComponent(term)
                    + '&types=country&language=en&limit=' + limit
                    + '&access_token=' + accessToken;
                const response = await fetch(url, { signal: pending.signal });
                if (!response.ok) {
                    return [];
                }
                const payload = await response.json();
                const names = (payload.features || [])
                    .map((feature) => feature.properties?.name)
                    .filter(Boolean);
                cache.set(term, names);
                return names;
            } catch (error) {
                return [];
            }
        }
    };
})();

function tagsInput(config = {}) {
    let initial = config.initial || config.skills || config.keywords || [];
    if (typeof initial === 'string') {
        initial = initial.split(',').map((tag) => tag.trim()).filter(Boolean);
    }

    return {
        tags: [...initial],
        inputValue: '',
        maxTags: config.maxTags || 10,
        fieldName: config.fieldName || 'tags',
        sectionOpen: true,
        suggestions: Array.isArray(config.suggestions) ? config.suggestions : [],
        // Opt in to catalogue-backed autocomplete instead of a static suggestions array.
        catalogSuggestions: config.catalogSuggestions === true,
        // Same, but the vocabulary is Mapbox country names.
        countrySuggestions: config.countrySuggestions === true,
        catalogResults: [],
        suggestTimer: null,
        feedback: config.feedback || null,
        focused: false,
        showSuggestions: false,

        get skills() { return this.tags; },
        get keywords() { return this.tags; },

        /* Autocomplete: suggestions not yet picked, filtered by the current input. */
        get filteredSuggestions() {
            if (this.catalogSuggestions || this.countrySuggestions) {
                return this.catalogResults.filter((s) => !this.hasTag(s)).slice(0, 8);
            }
            const query = this.inputValue.trim().toLowerCase();
            return this.suggestions
                .filter((s) => !this.hasTag(s))
                .filter((s) => query === '' || s.toLowerCase().includes(query))
                .slice(0, 8);
        },

        searchSuggestions(value) {
            if (value !== undefined && value !== null) {
                this.inputValue = value;
            }
            if (!this.catalogSuggestions && !this.countrySuggestions) {
                this.showSuggestions = this.canAdd() && this.filteredSuggestions.length > 0;
                return;
            }
            window.clearTimeout(this.suggestTimer);
            const query = this.inputValue.trim();
            this.suggestTimer = window.setTimeout(async () => {
                if (this.countrySuggestions) {
                    // No equivalent of the catalogue's "top": an empty box offers nothing rather
                    // than a list of countries nobody asked about.
                    this.catalogResults = await countryCatalog.suggest(query);
                } else {
                    const entries = query === ''
                        ? await skillCatalog.top(8)
                        : await skillCatalog.suggest(query, 8);
                    this.catalogResults = skillCatalog.labels(entries);
                }
                this.showSuggestions = this.canAdd() && this.filteredSuggestions.length > 0;
            }, 200);
        },

        selectSuggestion(suggestion) {
            this.addTag(suggestion);
            this.showSuggestions = false;
        },

        /* Tags are case-insensitive: "devOps" and "devops" are the same tag. */
        hasTag(value) {
            const lower = String(value).toLowerCase();
            return this.tags.some((tag) => tag.toLowerCase() === lower);
        },

        addTag(tagValue = null) {
            const value = (tagValue || this.inputValue).trim();
            if (value && !this.hasTag(value) && this.tags.length < this.maxTags) {
                this.tags.push(value);
                this.inputValue = '';
            }
            this.showSuggestions = false;
        },

        removeTag(index) {
            this.tags.splice(index, 1);
        },

        removeLast() {
            if (this.inputValue === '' && this.tags.length > 0) {
                this.tags.pop();
            }
        },

        canAdd() {
            return this.tags.length < this.maxTags;
        },

        getValue() {
            return this.tags.join(',');
        },

        /* Counter and message states, driven by the optional feedback config. */
        getCountClass() {
            const [low, high] = this.feedback?.optimal || [];
            if (this.tags.length === 0) return 'count-error';
            if (!low) return 'count-success';
            return this.tags.length < low || this.tags.length > high ? 'count-warning' : 'count-success';
        },

        getFeedback() {
            const messages = this.feedback?.messages;
            if (!messages) return null;
            const [low, high] = this.feedback.optimal || [1, this.maxTags];
            if (this.tags.length === 0) return { type: 'error', message: messages.empty };
            if (this.tags.length < low) return { type: 'warning', message: messages.low };
            if (this.tags.length > high) return { type: 'warning', message: messages.high };
            return { type: 'success', message: messages.optimal };
        },

        addSkill(value = null) { this.addTag(value); },
        removeSkill(index) { this.removeTag(index); },
        addKeyword(value = null) { this.addTag(value); },
        removeKeyword(index) { this.removeTag(index); }
    };
}

function fileUploader(config = {}) {
    return {
        isDragging: false,
        file: null,
        error: null,
        accept: config.accept || '*',
        maxSize: Number.parseInt(config.maxSize, 10) || 5 * 1024 * 1024,

        handleDragEnter(event) {
            event.preventDefault();
            this.isDragging = true;
        },

        handleDragLeave(event) {
            event.preventDefault();
            if (!this.$el?.contains(event.relatedTarget)) {
                this.isDragging = false;
            }
        },

        handleDrop(event) {
            event.preventDefault();
            this.isDragging = false;
            const files = event.dataTransfer?.files;
            if (files?.length) {
                this.processFile(files[0]);
                if (this.$refs.fileInput && !this.error) {
                    this.$refs.fileInput.files = files;
                }
            }
        },

        handleSelect(event) {
            const files = event.target.files;
            if (files?.length) {
                this.processFile(files[0]);
            } else {
                this.removeFile();
            }
        },

        processFile(file) {
            this.error = null;

            if (file.size > this.maxSize) {
                this.error = `File size must be less than ${this.formatSize(this.maxSize)}`;
                this.file = null;
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }
                return false;
            }

            if (!this.matchesAccept(file)) {
                this.error = 'Invalid file type';
                this.file = null;
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }
                return false;
            }

            this.file = file;
            return true;
        },

        matchesAccept(file) {
            if (!this.accept || this.accept === '*') {
                return true;
            }

            const acceptedTypes = this.accept.split(',').map((item) => item.trim()).filter(Boolean);
            return acceptedTypes.some((accepted) => {
                if (accepted.startsWith('.')) {
                    return file.name.toLowerCase().endsWith(accepted.toLowerCase());
                }
                if (accepted.endsWith('/*')) {
                    return file.type.startsWith(accepted.slice(0, -1));
                }
                return file.type === accepted;
            });
        },

        formatSize(bytes) {
            if (bytes < 1024) {
                return `${bytes} B`;
            }
            if (bytes < 1024 * 1024) {
                return `${(bytes / 1024).toFixed(1)} KB`;
            }
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        },

        getFileName() {
            return this.file?.name || '';
        },

        getFileSize() {
            return this.file ? this.formatSize(this.file.size) : '';
        },

        hasFile() {
            return this.file !== null;
        },

        removeFile() {
            this.file = null;
            this.error = null;
        }
    };
}

function jobsPage() {
    return {
        authenticated: false,
        // Desktop master-detail preview pane state.
        showPreviewPane: false,
        activeJobId: null,

        init() {
            this.authenticated = this.$el?.dataset.authenticated === 'true';
            // Remember the server-rendered placeholder so an empty search can restore it.
            const pane = document.getElementById('job-preview-pane');
            this._previewPlaceholder = pane ? pane.innerHTML : '';

            this.syncPreviewPane();
            this._resizeHandler = () => this.syncPreviewPane();
            window.addEventListener('resize', this._resizeHandler);

            const resultsEl = document.getElementById('job-results');
            if (resultsEl) {
                // Keep the preview pane in sync with the freshly swapped result set.
                // Only a swap of the result set itself: swaps inside it (e.g. the favorite toggle)
                // bubble up here too and must not reset the pane or close an open resume picker.
                resultsEl.addEventListener('htmx:afterSwap', (event) => {
                    if (event.target !== resultsEl) return;
                    this.previewFirst();
                });
            }

            // After applying from the pane the footer is replaced out of band; keep keyboard focus there.
            this._oobHandler = (event) => {
                const swapped = (event.detail && event.detail.target) || event.target;
                if (!swapped || swapped.id !== 'job-preview-apply') return;
                const applied = document.querySelector('#job-preview-apply.job-preview-apply--applied');
                if (applied) applied.focus({ preventScroll: true });
            };
            document.body.addEventListener('htmx:oobAfterSwap', this._oobHandler);

            // Populate the preview pane with the top result on first load (desktop only).
            this.$nextTick(() => this.previewFirst());
        },

        destroy() {
            window.removeEventListener('resize', this._resizeHandler);
            document.body.removeEventListener('htmx:oobAfterSwap', this._oobHandler);
        },

        // The preview pane is a desktop affordance only. Below 1280px there isn't room for
        // list + preview, so cards navigate to the full details page instead.
        syncPreviewPane() {
            this.showPreviewPane = window.matchMedia('(min-width: 1280px)').matches;
        },

        // Builds the preview endpoint URL from a card href, preserving the resume-perspective
        // query (?resumeId=...) so the pane's match context follows the selected resume.
        // Naive `href + '/preview'` would corrupt the path when a query string is present.
        previewUrl(href) {
            if (!href) return null;
            const qIndex = href.indexOf('?');
            if (qIndex === -1) {
                return href + '/preview';
            }
            return href.slice(0, qIndex) + '/preview' + href.slice(qIndex);
        },

        // Loads a job's compact preview into the side pane instead of navigating. Only active when
        // the preview pane is visible; otherwise the card's link navigates normally (mobile + a11y).
        previewJob(event, companyId, jobId) {
            if (!this.showPreviewPane) {
                return;
            }
            // Modified or non-primary clicks keep the browser's open-in-new-tab/window behaviour.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                return;
            }
            event.preventDefault();
            this.activeJobId = jobId;
            const card = event.currentTarget;
            const url = this.previewUrl(card && card.getAttribute ? card.getAttribute('href') : null)
                || ('/jobs/' + companyId + '/job/' + jobId + '/preview');
            if (typeof htmx !== 'undefined') {
                htmx.ajax('GET', url, { target: '#job-preview-pane', swap: 'innerHTML' });
            }
        },

        // Auto-previews the first result so the pane is never empty on desktop.
        previewFirst() {
            const firstCard = document.querySelector('#job-results .job-card-enhanced');
            if (!firstCard) {
                this.activeJobId = null;
                this.resetPreviewPane();
                return;
            }
            if (!this.showPreviewPane) {
                return;
            }
            const base = this.previewUrl(firstCard.getAttribute('href'));
            // auto=true: the pane fills itself with the top result; the server must not count a view.
            const url = base ? base + (base.includes('?') ? '&' : '?') + 'auto=true' : null;
            this.activeJobId = firstCard.dataset.jobId || null;
            if (url && typeof htmx !== 'undefined') {
                htmx.ajax('GET', url, { target: '#job-preview-pane', swap: 'innerHTML' });
            }
        },

        // An empty result set must not leave a job from the previous search in the pane.
        resetPreviewPane() {
            const pane = document.getElementById('job-preview-pane');
            if (pane && this._previewPlaceholder) {
                pane.innerHTML = this._previewPlaceholder;
            }
        },

    };
}

/**
 * Employer sourcing search page — desktop master-detail preview pane.
 *
 * Mirrors jobsPage()'s preview behaviour for the talent list: on wide screens (>=1280px) clicking
 * a candidate card loads its PII-free dossier preview into the sticky side pane without leaving the
 * list; on smaller screens the card navigates to the full candidate detail page. Inner controls
 * (Unlock / View / Save links and buttons) always behave normally. Card clicks are handled via
 * event delegation so the shared teaser-card template stays free of page-specific Alpine bindings
 * and keeps working when reused on the talent-pool page.
 */
function sourcingPage() {
    return {
        showPreviewPane: false,
        activeCandidateId: null,

        init() {
            this.syncPreviewPane();
            this._resizeHandler = () => this.syncPreviewPane();
            window.addEventListener('resize', this._resizeHandler);

            // Delegate card clicks from the Alpine root so HTMX-swapped cards keep working.
            this._clickHandler = (event) => {
                const card = event.target.closest('.teaser-card[data-candidate-id]');
                if (!card || !this.$el.contains(card)) return;
                // Let real controls (links, buttons, form fields) act normally.
                if (event.target.closest('a, button, input, label, form')) return;
                this.openCandidate(event, card.dataset.candidateId, card.dataset.postingId);
            };
            this.$el.addEventListener('click', this._clickHandler);

            // Re-preview the top result whenever the results list is swapped (filter/sort/AI search),
            // ignoring the preview pane's own swaps to avoid a loop.
            this._swapHandler = (evt) => {
                const tgt = evt.detail && evt.detail.target;
                if (!tgt || !this.$el.contains(tgt)) return;
                if (tgt.closest && tgt.closest('#talent-preview-pane')) return;
                this.previewFirst();
            };
            document.body.addEventListener('htmx:afterSwap', this._swapHandler);

            this.$nextTick(() => this.previewFirst());
        },

        destroy() {
            window.removeEventListener('resize', this._resizeHandler);
            this.$el.removeEventListener('click', this._clickHandler);
            document.body.removeEventListener('htmx:afterSwap', this._swapHandler);
        },

        syncPreviewPane() {
            this.showPreviewPane = window.matchMedia('(min-width: 1280px)').matches;
        },

        // Desktop: preview in the side pane. Smaller screens: navigate to the full detail page.
        openCandidate(event, jobSeekerId, postingId) {
            if (!jobSeekerId) return;
            if (!this.showPreviewPane) {
                let url = '/business/candidates/' + jobSeekerId + '?from=sourcing';
                if (postingId) {
                    url += '&posting=' + encodeURIComponent(postingId);
                }
                window.location.href = url;
                return;
            }
            if (event) event.preventDefault();
            this.previewCandidate(jobSeekerId);
        },

        // Clone the candidate's pre-rendered preview node (rendered inline by the server in
        // .talent-preview-store) into the sticky pane. No round-trip, so the preview always
        // reflects the exact fit evidence of the card that produced it.
        previewCandidate(jobSeekerId) {
            if (!this.showPreviewPane || !jobSeekerId) return;
            const pane = document.getElementById('talent-preview-pane');
            if (!pane) return;
            const sel = (window.CSS && CSS.escape) ? CSS.escape(jobSeekerId) : jobSeekerId;
            const node = this.$el.querySelector('.talent-preview-store [data-preview-for="' + sel + '"]');
            if (!node) return;
            this.activeCandidateId = jobSeekerId;
            pane.innerHTML = node.innerHTML;
            this.highlightActive();
        },

        previewFirst() {
            if (!this.showPreviewPane) return;
            const firstCard = this.$el.querySelector('.teaser-card[data-candidate-id]');
            if (!firstCard) {
                this.activeCandidateId = null;
                return;
            }
            this.previewCandidate(firstCard.dataset.candidateId);
        },

        highlightActive() {
            this.$el.querySelectorAll('.teaser-card[data-candidate-id]').forEach((card) => {
                card.classList.toggle('is-previewing', card.dataset.candidateId === this.activeCandidateId);
            });
        }
    };
}

/**
 * Save-search / job-alert button shown in the results intelligence bar.
 *
 * Guests are routed to sign-in (saving requires an account). Authenticated users persist the
 * current filter set via POST /jobs/saved-searches; on success the button flips to a "Saved"
 * confirmation. The button lives inside the HTMX-swapped results fragment, so its state is
 * intentionally per-render (a fresh search starts unsaved again).
 */
function saveSearchButton() {
    return {
        saving: false,
        saved: false,
        error: '',
        authenticated: false,

        init() {
            this.authenticated = document.querySelector('.jobs-page')?.dataset.authenticated === 'true';
        },

        save() {
            if (this.saved || this.saving) return;

            if (!this.authenticated) {
                // Preserve the current search so the user lands back here after signing in.
                const returnTo = window.location.pathname + window.location.search;
                window.location.href = '/auth/start?returnTo=' + encodeURIComponent(returnTo);
                return;
            }

            this.saving = true;
            this.error = '';
            // CSRF is exposed on the page as a hidden form input (not a meta tag), matching every
            // other state-changing form in the app.
            const csrfToken = document.querySelector('input[name="csrfToken"]')?.value
                || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                || '';
            const rawQuery = window.location.search.startsWith('?')
                ? window.location.search.slice(1)
                : window.location.search;
            const body = new URLSearchParams(rawQuery);
            body.set('csrfToken', csrfToken);

            fetch('/jobs/saved-searches', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    // The global CSRF filter validates the X-CSRF-Token header on form POSTs
                    // (HTMX adds it automatically; a plain fetch must set it explicitly).
                    'X-CSRF-Token': csrfToken
                },
                body: body.toString()
            })
                .then((response) => {
                    this.saving = false;
                    if (response.ok) {
                        this.saved = true;
                    } else if (response.status === 409) {
                        // Saved-search limit reached.
                        this.error = 'Limit reached';
                    } else {
                        this.error = 'Try again';
                    }
                })
                .catch(() => {
                    this.saving = false;
                    this.error = 'Try again';
                });
        }
    };
}

/**
 * Shared engine for the unified command-bar search (AI input + editable filter tokens +
 * dropdown pills). Used by both portals: employer sourcing (sourcingSearchBar) and the
 * jobseeker jobs search (jobsSearchBar).
 *
 * One Alpine state object drives three synchronized views:
 *   1. Tokens rendered inline in the search field (removable chips, one per active filter).
 *   2. Dropdown filter pills with panels.
 *   3. A hidden HTMX form whose inputs mirror the state; any change triggers a GET that swaps
 *      the results region instantly (no Search button) and pushes the URL.
 *
 * Free text is sent to the AI interpreter ONLY on explicit Enter. The response swaps the
 * results region and OOB-updates the intent-sync element with the interpreted filters, which
 * this component reads to sync tokens (no second search request). With AI disabled or down,
 * Enter degrades to a plain keyword search — the whole bar works without AI.
 *
 * The portal-specific spec must provide:
 *   intentSyncId          — DOM id of the OOB intent-sync element
 *   initFilters(dataset)  — seed filter state from the root element's data-* attributes
 *   resetFilterState()    — clear all filter state (not the AI note; the base handles that)
 *   get tokens            — removable chip list derived from the filter state
 *   removeToken(key)      — remove one filter by token key and apply
 *   applyIntent(filters)  — map the interpreted intent JSON onto the filter state
 *   setKeyword(text)      — set the plain keyword query (AI degradation path)
 * Optional hooks: onInit(), onDestroy(), onPanelOpen(name), currentContext() — when present, Enter
 * REFINES the live filters (they are sent as the AI conversation context) instead of replacing them.
 */
function commandSearchBar(spec) {
    const base = {
        freeText: '',
        openPanel: null,
        parsing: false,
        aiNote: '',
        aiEnabled: false,
        assistantContext: '{}',
        _debounceTimer: null,

        init() {
            const d = this.$el.dataset;
            this.aiEnabled = d.aiEnabled === 'true';
            this.initFilters(d);

            this._oobHandler = (evt) => {
                const el = evt.detail && evt.detail.target;
                if (el && el.id === this.intentSyncId) {
                    // htmx may pass the old node being replaced; always read the freshly
                    // swapped-in element from the DOM so we see the new data-* attributes.
                    const current = document.getElementById(this.intentSyncId);
                    if (current) this.syncFromIntent(current);
                }
            };
            document.body.addEventListener('htmx:oobAfterSwap', this._oobHandler);
            if (this.onInit) this.onInit();
        },

        destroy() {
            document.body.removeEventListener('htmx:oobAfterSwap', this._oobHandler);
            if (this.onDestroy) this.onDestroy();
        },

        get hasAnyFilter() {
            return this.tokens.length > 0;
        },

        // ---------- panels ----------

        togglePanel(name) {
            this.openPanel = this.openPanel === name ? null : name;
            if (this.openPanel && this.onPanelOpen) this.onPanelOpen(this.openPanel);
        },

        closePanel(name) {
            if (this.openPanel === name) this.openPanel = null;
        },

        // ---------- shared token/apply mechanics ----------

        onBackspace(event) {
            if (event.target.value !== '' || this.tokens.length === 0) return;
            event.preventDefault();
            this.removeToken(this.tokens[this.tokens.length - 1].key);
        },

        resetFilters() {
            this.resetFilterState();
            this.aiNote = '';
        },

        clearAll() {
            this.resetFilters();
            this.apply(true);
        },

        /** Debounced HTMX submit of the deterministic filter form. */
        apply(immediate = false) {
            clearTimeout(this._debounceTimer);
            // Resolve the form NOW: $refs walks up from the element that triggered the handler,
            // and a removed token's × button is detached from the DOM by the time $nextTick
            // fires — a deferred lookup would come back empty and silently skip the search.
            const form = this.$refs.filterForm;
            if (!form) return;
            // Still defer the submit itself so Alpine flushes :value/:disabled bindings first.
            const run = () => this.$nextTick(() => {
                if (typeof htmx !== 'undefined') htmx.trigger(form, 'submit');
                else form.requestSubmit();
            });
            if (immediate) run();
            else this._debounceTimer = setTimeout(run, 350);
        },

        // ---------- AI free-text interpretation ----------

        submitFreeText() {
            const text = this.freeText.trim();
            if (!text || this.parsing) return;
            if (!this.aiEnabled) {
                // No AI: the free text is a plain keyword query.
                this.setKeyword(text);
                this.freeText = '';
                this.apply(true);
                return;
            }
            if (this.currentContext) {
                // Refine: the live filters stay on screen and are the conversation context, so a
                // token removed since the last turn can never come back from an older AI echo.
                this.assistantContext = this.currentContext();
                this.aiNote = '';
            } else {
                // A new free-text query is a fresh interpretation: drop the previous tokens and
                // conversation context immediately so the bar never shows stale filters while parsing.
                this.resetFilters();
                this.assistantContext = '{}';
            }
            this.parsing = true;
            this.$nextTick(() => {
                const form = this.$refs.assistantForm;
                if (form && typeof htmx !== 'undefined') htmx.trigger(form, 'submit');
                else this.parsing = false;
            });
        },

        onAssistantDone(event) {
            this.parsing = false;
            const ok = event.detail && event.detail.successful;
            if (!ok) {
                // AI endpoint failed (timeout / 429 / 5xx): degrade to keyword search, never block.
                const text = this.freeText.trim();
                this.aiNote = 'AI assist is unavailable right now — searching by keywords instead.';
                if (text) {
                    this.setKeyword(text);
                    this.freeText = '';
                    this.apply(true);
                }
            }
            // The input was disabled while parsing; hand focus back for the next query.
            this.$nextTick(() => this.$refs.freeInput && this.$refs.freeInput.focus());
        },

        /** Reads the OOB-swapped interpreted intent and syncs tokens/pills (results already swapped). */
        syncFromIntent(el) {
            let filters = {};
            try {
                filters = (JSON.parse(el.dataset.intent || '{}').filters) || {};
            } catch (e) { /* malformed intent — keep current state */ }
            this.applyIntent(filters);
            this.assistantContext = el.dataset.context || '{}';
            this.aiNote = el.dataset.aiAvailable === 'false'
                ? 'AI assist is unavailable right now — searched by keywords instead.'
                : '';
            this.freeText = '';
            this.parsing = false;
        }
    };

    // Descriptor-based merge so `get tokens` (and other getters) survive; a plain spread or
    // Object.assign would invoke the getters instead of copying them.
    const merged = {};
    Object.defineProperties(merged, Object.getOwnPropertyDescriptors(base));
    Object.defineProperties(merged, Object.getOwnPropertyDescriptors(spec));
    return merged;
}

function splitCsvList(value) {
    return (value || '').split(',').map((s) => s.trim()).filter(Boolean);
}

/** Employment types the jobs filter knows, keyed by the enum name the server speaks. */
const EMPLOYMENT_TYPE_LABELS = {
    FULL_TIME: 'Full-time',
    PART_TIME: 'Part-time',
    CONTRACT: 'Contract',
    TEMPORARY: 'Temporary',
    SEASONAL: 'Seasonal',
    INTERNSHIP: 'Internship'
};

/**
 * Employer sourcing — unified command bar. Portal spec on top of commandSearchBar():
 * skills / experience / work format / location (free text) / availability / salary range.
 */
function sourcingSearchBar() {
    return commandSearchBar({
        intentSyncId: 'talent-intent-sync',

        q: '',
        skills: [],
        location: '',
        workFormats: [],
        minExperience: '',
        minSalary: '',
        maxSalary: '',
        startWork: '',

        skillDraft: '',
        locationDraft: '',
        minSalaryDraft: '',
        maxSalaryDraft: '',

        initFilters(d) {
            this.q = d.q || '';
            this.skills = splitCsvList(d.skills);
            this.location = d.location || '';
            this.workFormats = splitCsvList(d.workFormats);
            this.minExperience = d.minExperience || '';
            this.minSalary = d.minSalary || '';
            this.maxSalary = d.maxSalary || '';
            this.startWork = d.startWork || '';
            this.locationDraft = this.location;
            this.minSalaryDraft = this.minSalary;
            this.maxSalaryDraft = this.maxSalary;
        },

        onInit() {
            this._clearSkillsHandler = () => {
                if (this.skills.length > 0) {
                    this.skills = [];
                    this.apply(true);
                }
            };
            document.body.addEventListener('talent:clear-skills', this._clearSkillsHandler);
        },

        onDestroy() {
            document.body.removeEventListener('talent:clear-skills', this._clearSkillsHandler);
        },

        onPanelOpen(name) {
            if (name === 'location') this.locationDraft = this.location;
            if (name === 'salary') {
                this.minSalaryDraft = this.minSalary;
                this.maxSalaryDraft = this.maxSalary;
            }
        },

        // ---------- derived labels ----------

        get tokens() {
            const t = [];
            if (this.q) t.push({ key: 'q', label: 'Query: ' + this.q });
            this.skills.forEach((s, i) => t.push({ key: 'skill:' + i, label: s }));
            if (this.minExperience) t.push({ key: 'experience', label: this.minExperience + '+ yrs' });
            this.workFormats.forEach((f) => t.push({ key: 'format:' + f, label: this.formatLabel(f) }));
            if (this.location) t.push({ key: 'location', label: this.location });
            if (this.startWork) t.push({ key: 'startWork', label: this.availabilityLabel });
            if (this.minSalary || this.maxSalary) t.push({ key: 'salary', label: this.salaryPillLabel });
            return t;
        },

        get skillsPillLabel() {
            if (this.skills.length === 0) return '';
            return this.skills.length === 1 ? this.skills[0] : this.skills[0] + ' +' + (this.skills.length - 1);
        },

        get formatPillLabel() {
            return this.workFormats.map((f) => this.formatLabel(f)).join(', ');
        },

        get availabilityLabel() {
            return { IMMEDIATELY: 'Available now', WEEKS_2: 'Within 2 weeks', MONTHS_1: 'Within 1 month' }[this.startWork] || '';
        },

        get salaryPillLabel() {
            const fmt = (v) => '$' + Math.round(Number(v) / 1000) + 'k';
            if (this.minSalary && this.maxSalary) return fmt(this.minSalary) + '–' + fmt(this.maxSalary);
            if (this.minSalary) return fmt(this.minSalary) + '+';
            if (this.maxSalary) return 'up to ' + fmt(this.maxSalary);
            return '';
        },

        formatLabel(f) {
            return { REMOTE: 'Remote', HYBRID: 'Hybrid', ON_SITE: 'On-site' }[f] || f;
        },

        // ---------- state mutations (all instant-apply) ----------

        removeToken(key) {
            if (key === 'q') this.q = '';
            else if (key === 'experience') this.minExperience = '';
            else if (key === 'location') { this.location = ''; this.locationDraft = ''; }
            else if (key === 'startWork') this.startWork = '';
            else if (key === 'salary') { this.minSalary = ''; this.maxSalary = ''; this.minSalaryDraft = ''; this.maxSalaryDraft = ''; }
            else if (key.startsWith('skill:')) this.skills.splice(Number(key.slice(6)), 1);
            else if (key.startsWith('format:')) this.workFormats = this.workFormats.filter((f) => f !== key.slice(7));
            this.apply(true);
        },

        resetFilterState() {
            this.q = '';
            this.skills = [];
            this.location = '';
            this.locationDraft = '';
            this.workFormats = [];
            this.minExperience = '';
            this.minSalary = '';
            this.maxSalary = '';
            this.minSalaryDraft = '';
            this.maxSalaryDraft = '';
            this.startWork = '';
        },

        setKeyword(text) {
            this.q = text;
        },

        addSkill() {
            const value = this.skillDraft.trim().replace(/,+$/, '');
            this.skillDraft = '';
            if (!value || this.skills.length >= 10) return;
            if (this.skills.some((s) => s.toLowerCase() === value.toLowerCase())) return;
            this.skills.push(value);
            this.apply();
        },

        toggleSkill(skill) {
            const idx = this.skills.findIndex((s) => s.toLowerCase() === skill.toLowerCase());
            if (idx >= 0) this.skills.splice(idx, 1);
            else if (this.skills.length < 10) this.skills.push(skill);
            this.apply();
        },

        removeSkill(index) {
            this.skills.splice(index, 1);
            this.apply();
        },

        setExperience(value) {
            this.minExperience = value;
            this.openPanel = null;
            this.apply(true);
        },

        toggleFormat(format) {
            this.workFormats = this.workFormats.includes(format)
                ? this.workFormats.filter((f) => f !== format)
                : [...this.workFormats, format];
            this.apply();
        },

        setStartWork(value) {
            this.startWork = value;
            this.openPanel = null;
            this.apply(true);
        },

        applyLocation() {
            this.location = this.locationDraft.trim();
            this.openPanel = null;
            this.apply(true);
        },

        setSalary(min, max) {
            this.minSalaryDraft = min;
            this.maxSalaryDraft = max;
            this.applySalary();
        },

        applySalary() {
            this.minSalary = String(this.minSalaryDraft || '').trim();
            this.maxSalary = String(this.maxSalaryDraft || '').trim();
            this.openPanel = null;
            this.apply(true);
        },

        /** Maps the interpreted employer intent onto the filter state. */
        applyIntent(filters) {
            this.q = filters.query || '';
            this.skills = Array.isArray(filters.skills) ? filters.skills.slice(0, 10) : [];
            this.location = filters.place || '';
            this.locationDraft = this.location;
            const formats = [];
            if (filters.remote) formats.push('REMOTE');
            if (filters.hybrid) formats.push('HYBRID');
            if (filters.onsite) formats.push('ON_SITE');
            this.workFormats = formats;
            this.minExperience = filters.experience != null ? String(filters.experience) : '';
            this.minSalary = '';
            this.minSalaryDraft = '';
            this.maxSalary = filters.salaryMax != null ? String(filters.salaryMax) : '';
            this.maxSalaryDraft = this.maxSalary;
            this.startWork = filters.startWork || '';
        }
    });
}

/**
 * Jobseeker /jobs — unified command bar. Portal spec on top of commandSearchBar():
 * keyword / skills / location (Mapbox geo + radius) / work format (onSite|remote|hybrid) /
 * experience / salary (ANNUAL|HOURLY min presets).
 *
 * The location pill embeds the shared Mapbox location-input fragment, which owns the geo
 * hidden fields (country/region/place/lat/lon/placeId) pulled into the filter GET via
 * hx-include. This component mirrors only the display label (`place`) and reacts to the
 * fragment's `posting-location-changed` events to keep tokens and results in sync.
 */
function jobsSearchBar() {
    return commandSearchBar({
        intentSyncId: 'jobs-intent-sync',

        q: '',
        skills: [],
        place: '',
        radius: '',
        onSite: false,
        remote: false,
        hybrid: false,
        employmentTypes: [],
        experience: '',
        salaryType: 'ANNUAL',
        salaryMin: '',

        skillDraft: '',
        salaryMinDraft: '',

        salaryPresets: {
            ANNUAL: [
                { label: '$50k+', value: 50000 },
                { label: '$75k+', value: 75000 },
                { label: '$100k+', value: 100000 },
                { label: '$150k+', value: 150000 }
            ],
            HOURLY: [
                { label: '$25+', value: 25 },
                { label: '$50+', value: 50 },
                { label: '$75+', value: 75 },
                { label: '$100+', value: 100 }
            ]
        },

        initFilters(d) {
            this.q = d.q || '';
            this.skills = splitCsvList(d.skills);
            this.place = d.place || '';
            this.radius = d.radius || '';
            this.onSite = d.onSite === 'true';
            this.remote = d.remote === 'true';
            this.hybrid = d.hybrid === 'true';
            this.employmentTypes = splitCsvList(d.employmentTypes).filter((t) => t in EMPLOYMENT_TYPE_LABELS);
            this.experience = d.experience || '';
            this.salaryType = d.salaryType || 'ANNUAL';
            this.salaryMin = d.salaryMin || '';
            this.salaryMinDraft = this.salaryMin;
        },

        onInit() {
            // The Mapbox fragment dispatches this on typing, suggestion pick, and clear.
            // Mirror the committed place label and instant-apply (debounced) like other filters.
            this._locationHandler = () => {
                this.$nextTick(() => {
                    const comp = this._locationComponent();
                    this.place = comp ? (comp.place || '') : '';
                    this.apply();
                });
            };
            this.$el.addEventListener('posting-location-changed', this._locationHandler);

            // Zero-results empty state dispatches removal requests for the narrowing filter.
            this._removeFilterHandler = (evt) => {
                const key = evt.detail && evt.detail.filter;
                if (key) this.removeToken(key);
            };
            document.body.addEventListener('jobs:remove-filter', this._removeFilterHandler);
        },

        onDestroy() {
            this.$el.removeEventListener('posting-location-changed', this._locationHandler);
            document.body.removeEventListener('jobs:remove-filter', this._removeFilterHandler);
        },

        onPanelOpen(name) {
            if (name === 'salary') this.salaryMinDraft = this.salaryMin;
        },

        _locationComponent() {
            // $el is the element whose handler is running (a token ×, Clear all), not the bar root.
            const el = this.$root.querySelector('.location-autocomplete');
            return el && window.Alpine ? window.Alpine.$data(el) : null;
        },

        /** Sets the Mapbox fragment's state without dispatching (no extra search request). */
        _setLocationSilently(place) {
            this.place = place || '';
            const comp = this._locationComponent();
            if (!comp) return;
            comp.country = '';
            comp.region = '';
            comp.place = this.place;
            comp.latitude = '';
            comp.longitude = '';
            comp.placeId = '';
            comp.query = this.place;
            comp.suggestions = [];
            comp.showSuggestions = false;
            comp.selected = !!this.place;
        },

        // ---------- derived labels ----------

        get tokens() {
            const t = [];
            if (this.q) t.push({ key: 'q', label: 'Query: ' + this.q });
            this.skills.forEach((s, i) => t.push({ key: 'skill:' + i, label: s }));
            if (this.experience) t.push({ key: 'experience', label: this.experience + '+ yrs' });
            ['onSite', 'remote', 'hybrid'].filter((f) => this[f])
                .forEach((f) => t.push({ key: 'format:' + f, label: this.formatLabel(f) }));
            this.employmentTypes.forEach((type) => t.push({
                key: 'employmentType:' + type,
                label: EMPLOYMENT_TYPE_LABELS[type]
            }));
            if (this.place) t.push({ key: 'location', label: this.place });
            if (this.hasSalary) t.push({ key: 'salary', label: this.salaryPillLabel });
            return t;
        },

        get hasSalary() {
            return Number(this.salaryMin) > 0;
        },

        get skillsPillLabel() {
            if (this.skills.length === 0) return '';
            return this.skills.length === 1 ? this.skills[0] : this.skills[0] + ' +' + (this.skills.length - 1);
        },

        get formatPillLabel() {
            return ['onSite', 'remote', 'hybrid'].filter((f) => this[f]).map((f) => this.formatLabel(f)).join(', ');
        },

        get employmentTypePillLabel() {
            return this.employmentTypes.map((type) => EMPLOYMENT_TYPE_LABELS[type]).join(', ');
        },

        get radiusLabel() {
            return { 10: '~5 mi', 25: '~15 mi', 50: '~30 mi', 100: '~60 mi', 250: '~150 mi' }[this.radius] || '';
        },

        get locationPillLabel() {
            if (!this.place) return '';
            return this.radiusLabel ? this.place + ' · ' + this.radiusLabel : this.place;
        },

        get salaryPillLabel() {
            if (!this.hasSalary) return '';
            const min = Number(this.salaryMin);
            if (this.salaryType === 'HOURLY') return '$' + min + '+/hr';
            return min >= 1000 ? '$' + Math.round(min / 1000) + 'k+' : '$' + min + '+';
        },

        get currentSalaryPresets() {
            return this.salaryPresets[this.salaryType] || this.salaryPresets.ANNUAL;
        },

        formatLabel(f) {
            return { onSite: 'On-site', remote: 'Remote', hybrid: 'Hybrid' }[f] || f;
        },

        // ---------- state mutations (all instant-apply) ----------

        removeToken(key) {
            if (key === 'q') this.q = '';
            else if (key === 'experience') this.experience = '';
            else if (key === 'salary') { this.salaryMin = ''; this.salaryMinDraft = ''; }
            else if (key === 'location' || key === 'place') { this._setLocationSilently(''); this.radius = ''; }
            else if (key === 'skills') this.skills = [];
            else if (key === 'format') { this.onSite = false; this.remote = false; this.hybrid = false; }
            else if (key === 'employmentType') this.employmentTypes = [];
            else if (key.startsWith('skill:')) this.skills.splice(Number(key.slice(6)), 1);
            else if (key.startsWith('format:')) this[key.slice(7)] = false;
            else if (key.startsWith('employmentType:')) {
                const removed = key.slice('employmentType:'.length);
                this.employmentTypes = this.employmentTypes.filter((type) => type !== removed);
            }
            this.apply(true);
        },

        resetFilterState() {
            this.q = '';
            this.skills = [];
            this.skillDraft = '';
            this.radius = '';
            this.onSite = false;
            this.remote = false;
            this.hybrid = false;
            this.employmentTypes = [];
            this.experience = '';
            this.salaryType = 'ANNUAL';
            this.salaryMin = '';
            this.salaryMinDraft = '';
            this._setLocationSilently('');
        },

        /** The live filters as AI conversation context (same keys as the server's context echo). */
        currentContext() {
            return JSON.stringify({
                query: this.q || null,
                place: this.place || null,
                skills: this.skills,
                experience: this.experience ? Number(this.experience) : null,
                salaryType: this.hasSalary ? this.salaryType : null,
                salaryMin: this.hasSalary ? Number(this.salaryMin) : null,
                remote: this.remote,
                hybrid: this.hybrid,
                employmentTypes: this.employmentTypes
            });
        },

        setKeyword(text) {
            // Enter refines: a new word narrows the keyword query instead of replacing it.
            if (!this.q) { this.q = text; return; }
            if ((' ' + this.q.toLowerCase() + ' ').includes(' ' + text.toLowerCase() + ' ')) return;
            this.q = this.q + ' ' + text;
        },

        addSkill() {
            const value = this.skillDraft.trim().replace(/,+$/, '');
            this.skillDraft = '';
            if (!value || this.skills.length >= 10) return;
            if (this.skills.some((s) => s.toLowerCase() === value.toLowerCase())) return;
            this.skills.push(value);
            this.apply();
        },

        toggleSkill(skill) {
            const idx = this.skills.findIndex((s) => s.toLowerCase() === skill.toLowerCase());
            if (idx >= 0) this.skills.splice(idx, 1);
            else if (this.skills.length < 10) this.skills.push(skill);
            this.apply();
        },

        removeSkill(index) {
            this.skills.splice(index, 1);
            this.apply();
        },

        setExperience(value) {
            this.experience = value;
            this.openPanel = null;
            this.apply(true);
        },

        toggleFormat(format) {
            if (format !== 'onSite' && format !== 'remote' && format !== 'hybrid') return;
            this[format] = !this[format];
            this.apply();
        },

        toggleEmploymentType(type) {
            if (!(type in EMPLOYMENT_TYPE_LABELS)) return;
            this.employmentTypes = this.employmentTypes.includes(type)
                ? this.employmentTypes.filter((t) => t !== type)
                : [...this.employmentTypes, type];
            this.apply();
        },

        setSalaryType(type) {
            if (type !== 'ANNUAL' && type !== 'HOURLY') return;
            this.salaryType = type;
            // Presets differ per type; a previously chosen min no longer means the same thing.
            this.salaryMin = '';
            this.salaryMinDraft = '';
            this.apply(true);
        },

        selectSalaryPreset(value) {
            this.salaryMin = value > 0 ? String(value) : '';
            this.salaryMinDraft = this.salaryMin;
            this.openPanel = null;
            this.apply(true);
        },

        applySalary() {
            const min = Number(this.salaryMinDraft);
            this.salaryMin = Number.isFinite(min) && min > 0 ? String(min) : '';
            this.openPanel = null;
            this.apply(true);
        },

        /** Maps the interpreted jobseeker intent onto the filter state. */
        applyIntent(filters) {
            this.q = filters.query || '';
            this.skills = Array.isArray(filters.skills) ? filters.skills.slice(0, 10) : [];
            this.experience = filters.experience != null ? String(filters.experience) : '';
            this.onSite = false;
            this.remote = !!filters.remote;
            this.hybrid = !!filters.hybrid;
            this.employmentTypes = Array.isArray(filters.employmentTypes)
                ? filters.employmentTypes.filter((t) => t in EMPLOYMENT_TYPE_LABELS)
                : [];
            this.salaryType = filters.salaryType === 'HOURLY' ? 'HOURLY' : 'ANNUAL';
            this.salaryMin = filters.salaryMin != null ? String(filters.salaryMin) : '';
            this.salaryMinDraft = this.salaryMin;
            this.radius = '';
            this._setLocationSilently(filters.place || '');
        }
    });
}

/**
 * Jobseeker /companies — unified command bar. Portal spec on top of commandSearchBar():
 * company name keyword / location (Mapbox geo, no radius) / work format (remote|hybrid) /
 * industry keywords / "Hiring now" and "Answers fast" toggle pills.
 *
 * No AI layer on this bar: the company-directory intent space is small enough that the
 * pills cover it. Enter on free text degrades to a plain name/keyword search via the
 * commandSearchBar() base (aiEnabled stays false).
 */
function companiesSearchBar() {
    return commandSearchBar({
        intentSyncId: 'companies-intent-sync',

        q: '',
        place: '',
        keywords: [],
        remote: false,
        hybrid: false,
        hiring: false,
        answersFast: false,

        keywordDraft: '',

        initFilters(d) {
            this.q = d.q || '';
            this.place = d.place || '';
            this.keywords = splitCsvList(d.keywords);
            this.remote = d.remote === 'true';
            this.hybrid = d.hybrid === 'true';
            this.hiring = d.hiring === 'true';
            this.answersFast = d.answersFast === 'true';
        },

        onInit() {
            // The Mapbox fragment dispatches this on typing, suggestion pick, and clear.
            this._locationHandler = () => {
                this.$nextTick(() => {
                    const comp = this._locationComponent();
                    this.place = comp ? (comp.place || '') : '';
                    this.apply();
                });
            };
            this.$el.addEventListener('posting-location-changed', this._locationHandler);

            // Zero-results empty state dispatches removal requests for the narrowing filter.
            this._removeFilterHandler = (evt) => {
                const key = evt.detail && evt.detail.filter;
                if (key) this.removeToken(key);
            };
            document.body.addEventListener('companies:remove-filter', this._removeFilterHandler);
        },

        onDestroy() {
            this.$el.removeEventListener('posting-location-changed', this._locationHandler);
            document.body.removeEventListener('companies:remove-filter', this._removeFilterHandler);
        },

        _locationComponent() {
            // $el is the element whose handler is running (a token ×, Clear all), not the bar root.
            const el = this.$root.querySelector('.location-autocomplete');
            return el && window.Alpine ? window.Alpine.$data(el) : null;
        },

        /** Sets the Mapbox fragment's state without dispatching (no extra search request). */
        _setLocationSilently(place) {
            this.place = place || '';
            const comp = this._locationComponent();
            if (!comp) return;
            comp.country = '';
            comp.region = '';
            comp.place = this.place;
            comp.latitude = '';
            comp.longitude = '';
            comp.placeId = '';
            comp.query = this.place;
            comp.suggestions = [];
            comp.showSuggestions = false;
            comp.selected = !!this.place;
        },

        // ---------- derived labels ----------

        get tokens() {
            const t = [];
            if (this.q) t.push({ key: 'q', label: this.q });
            this.keywords.forEach((k, i) => t.push({ key: 'keyword:' + i, label: k }));
            if (this.place) t.push({ key: 'location', label: this.place });
            if (this.remote) t.push({ key: 'format:remote', label: 'Remote' });
            if (this.hybrid) t.push({ key: 'format:hybrid', label: 'Hybrid' });
            if (this.hiring) t.push({ key: 'hiring', label: 'Hiring now' });
            if (this.answersFast) t.push({ key: 'answers', label: 'Answers fast' });
            return t;
        },

        get industryPillLabel() {
            if (this.keywords.length === 0) return '';
            return this.keywords.length === 1
                ? this.keywords[0]
                : this.keywords[0] + ' +' + (this.keywords.length - 1);
        },

        get formatPillLabel() {
            return [this.remote ? 'Remote' : '', this.hybrid ? 'Hybrid' : ''].filter(Boolean).join(', ');
        },

        // ---------- state mutations (all instant-apply) ----------

        removeToken(key) {
            if (key === 'q') this.q = '';
            else if (key === 'location' || key === 'place') this._setLocationSilently('');
            else if (key === 'hiring') this.hiring = false;
            else if (key === 'answers') this.answersFast = false;
            else if (key === 'keywords') this.keywords = [];
            else if (key.startsWith('keyword:')) this.keywords.splice(Number(key.slice(8)), 1);
            else if (key.startsWith('format:')) this[key.slice(7)] = false;
            this.apply(true);
        },

        resetFilterState() {
            this.q = '';
            this.keywords = [];
            this.keywordDraft = '';
            this.remote = false;
            this.hybrid = false;
            this.hiring = false;
            this.answersFast = false;
            this._setLocationSilently('');
        },

        setKeyword(text) {
            this.q = text;
        },

        addKeyword() {
            const value = this.keywordDraft.trim().replace(/,+$/, '');
            this.keywordDraft = '';
            if (!value || this.keywords.length >= 10) return;
            if (this.keywords.some((k) => k.toLowerCase() === value.toLowerCase())) return;
            this.keywords.push(value);
            this.apply();
        },

        toggleKeyword(keyword) {
            const idx = this.keywords.findIndex((k) => k.toLowerCase() === keyword.toLowerCase());
            if (idx >= 0) this.keywords.splice(idx, 1);
            else if (this.keywords.length < 10) this.keywords.push(keyword);
            this.apply();
        },

        removeKeyword(index) {
            this.keywords.splice(index, 1);
            this.apply();
        },

        toggleFormat(format) {
            if (format !== 'remote' && format !== 'hybrid') return;
            this[format] = !this[format];
            this.apply();
        },

        toggleHiring() {
            this.hiring = !this.hiring;
            this.apply(true);
        },

        toggleAnswersFast() {
            this.answersFast = !this.answersFast;
            this.apply(true);
        },

        /** No AI layer on this bar — nothing to map. */
        applyIntent() {}
    });
}

// Resume perspective switcher on the /jobs list ("Matching as"). Mirrors sortDropdown's URL
// handling: the default resume maps to a clean URL (no resumeId param), any other resume sets
// resumeId, and the swap goes through HTMX so only the results region re-renders.
function resumeSwitcher() {
    return {
        apply(resumeId) {
            const params = new URLSearchParams(window.location.search);
            if (resumeId) {
                params.set('resumeId', resumeId);
            } else {
                params.delete('resumeId');
            }
            params.delete('page');

            const queryString = params.toString();
            const url = '/jobs' + (queryString ? '?' + queryString : '');
            const target = document.getElementById('job-results');

            if (target && typeof htmx !== 'undefined') {
                htmx.ajax('GET', url, { target, swap: 'innerHTML' });
                window.history.pushState({}, '', url);
            } else {
                window.location.href = url;
            }
        }
    };
}

function sortDropdown(config = {}) {
    const options = typeof config === 'boolean' ? { isAuthenticated: config } : config;
    const params = new URLSearchParams(window.location.search);
    // Every option defaults to the /jobs behaviour, so existing callers are unchanged.
    // basePath/defaultSort/currentSort/labels/target/select/swap let other lists (the moderator queue) reuse it.
    const basePath = options.basePath || '/jobs';
    const defaultSort = options.defaultSort || 'relevance';
    const initialSort = options.currentSort || params.get('sort') || defaultSort;
    const isAuthenticated = options.isAuthenticated || false;

    return {
        open: false,
        currentSort: initialSort,
        isAuthenticated,

        labels: {
            relevance: isAuthenticated ? 'Best Match' : 'Recommended',
            newest: 'Newest First',
            salary_high: 'Salary: High to Low',
            salary_low: 'Salary: Low to High',
            ...(options.labels || {})
        },

        getLabel() {
            if (defaultSort === 'relevance') {
                if (this.currentSort === 'relevance') {
                    return this.isAuthenticated ? 'Best Match' : 'Recommended';
                }
                return this.labels[this.currentSort] || (this.isAuthenticated ? 'Best Match' : 'Recommended');
            }
            return this.labels[this.currentSort] || this.labels[defaultSort];
        },

        isActive(sortValue) {
            return this.currentSort === sortValue || (sortValue === defaultSort && !this.currentSort);
        },

        buildSortUrl(sortValue) {
            const sortParams = new URLSearchParams(window.location.search);
            if (sortValue && sortValue !== defaultSort) {
                sortParams.set('sort', sortValue);
            } else {
                sortParams.delete('sort');
            }
            sortParams.delete('page');

            const queryString = sortParams.toString();
            return basePath + (queryString ? '?' + queryString : '');
        },

        applySort(sortValue) {
            this.currentSort = sortValue;
            const url = this.buildSortUrl(sortValue);
            const target = document.querySelector(options.target || '#job-results');

            if (target && typeof htmx !== 'undefined') {
                // htmx.ajax() without a source puts .htmx-request on <body>, so a dropdown that declares
                // hx-indicator on its root is made the source: its indicator then gets the class itself.
                const root = this.$root;
                const indicatorSource = root && root.hasAttribute('hx-indicator') ? { source: root } : {};
                htmx.ajax('GET', url, {
                    target,
                    ...indicatorSource,
                    swap: options.swap || 'innerHTML',
                    ...(options.select ? { select: options.select } : {})
                });
                window.history.pushState({}, '', url);
            } else {
                window.location.href = url;
            }
        }
    };
}

function evaluationForm(config = {}) {
    const parseJson = (value, fallback) => {
        if (value === undefined || value === null || value === '') {
            return fallback;
        }
        if (typeof value !== 'string') {
            return value;
        }
        try {
            return JSON.parse(value);
        } catch (error) {
            return fallback;
        }
    };

    const criteria = Array.isArray(config.criteria)
        ? config.criteria
        : parseJson(config.criteriaJson, []);
    const existingSource = config.existing && typeof config.existing === 'object'
        ? config.existing
        : parseJson(config.existingRatingsJson, {});
    const existing = existingSource && typeof existingSource === 'object' ? existingSource : {};
    const recommendation = config.recommendation !== undefined
        ? config.recommendation
        : parseJson(config.existingRecommendationJson, '');
    const notes = config.notes !== undefined
        ? config.notes
        : parseJson(config.existingNotesJson, '');

    return {
        criteria,
        ratings: { ...existing },
        recommendation: recommendation || '',
        notes: notes || '',

        getRating(name) {
            return Number.parseInt(this.ratings[name], 10) || 0;
        },

        setRating(name, value) {
            const criterion = this.criteria.find((item) => item.name === name);
            const maxScore = Number.parseInt(criterion?.maxScore, 10) || 5;
            const score = Math.max(0, Math.min(Number.parseInt(value, 10) || 0, maxScore));

            if (score > 0) {
                this.ratings[name] = score;
            } else {
                delete this.ratings[name];
            }
        },

        ratingsJson() {
            return JSON.stringify(this.ratings);
        },

        syncMarkdownEditors(form) {
            const root = form || this.$el;
            if (!root) return;

            root.querySelectorAll('textarea[data-markdown]').forEach((textarea) => {
                const group = textarea.closest('.form-group') || root;
                const codeMirror = group.querySelector('.CodeMirror');
                if (codeMirror?.CodeMirror) {
                    textarea.value = codeMirror.CodeMirror.getValue();
                    if (textarea.name === 'notes') {
                        this.notes = textarea.value;
                    }
                    textarea.dispatchEvent(new Event('input', { bubbles: true }));
                }
            });
        },

        recommendationClass(recommendation) {
            return {
                STRONG_YES: 'rec-strong-yes',
                YES: 'rec-yes',
                MAYBE: 'rec-maybe',
                NO: 'rec-no',
                STRONG_NO: 'rec-strong-no'
            }[recommendation] || '';
        },

        recommendationLabel(recommendation) {
            return {
                STRONG_YES: 'Strong yes',
                YES: 'Yes',
                MAYBE: 'Maybe',
                NO: 'No',
                STRONG_NO: 'Strong no'
            }[recommendation] || recommendation;
        },

        isValid() {
            if (!this.recommendation) {
                return false;
            }

            return this.criteria.every((criterion) => {
                const score = this.getRating(criterion.name);
                const maxScore = Number.parseInt(criterion.maxScore, 10) || 0;
                return score > 0 && (maxScore === 0 || score <= maxScore);
            });
        }
    };
}

function starRating(config = {}) {
    return {
        max: Number.parseInt(config.max, 10) || 5,
        value: Number.parseInt(config.value, 10) || 0,
        hovering: 0,

        selectRating(value) {
            this.value = Number.parseInt(value, 10) || 0;
        },

        isActive(index) {
            const activeValue = this.hovering || this.value;
            return index <= activeValue;
        },

        label() {
            const activeValue = this.hovering || this.value;
            if (!activeValue) {
                return `Not rated / ${this.max}`;
            }

            return `${activeValue} / ${this.max}`;
        }
    };
}

/**
 * Share button: Web Share API with clipboard fallback.
 *
 * `share(title, text, url)` shares `url` (resolved against the current page, so a
 * relative path works) or the current page when `url` is omitted. Pass dynamic values
 * through data-* attributes, never through string literals inside @click:
 *
 *   <button x-data="shareButton()" data-share-url="/jobs/.." data-share-title="..."
 *           @click="share($el.dataset.shareTitle, 'Text', $el.dataset.shareUrl)">
 */
function shareButton() {
    return {
        copied: false,

        async share(title, text, url) {
            const target = url ? new URL(url, window.location.href).href : window.location.href;

            if (navigator.share) {
                try {
                    await navigator.share({ title, text, url: target });
                    return;
                } catch (err) {
                    // The user closed the share sheet: do nothing. Any other error falls back to the clipboard.
                    if (err.name === 'AbortError') return;
                }
            }

            try {
                await navigator.clipboard.writeText(target);
            } catch (err) {
                // Older browsers / insecure context
                const textArea = document.createElement('textarea');
                textArea.value = target;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            this.copied = true;
            setTimeout(() => { this.copied = false; }, 2000);
        }
    };
}

/**
 * Interviewer meeting-link editor (propose-slots + assignment details).
 * Lives in the always-loaded bundle: page scripts that arrive after hx-boost swaps run after
 * Alpine has initialised the scope and cause a ReferenceError (see avatarUploader, bug 525).
 * Contract with the templates: link, saving, copied, saveLink(interviewId), copyLink().
 * Server contract: POST /interviewer/assignments/{id}/meeting-link returns a feedback fragment.
 */
function meetingLinkManager(initialLink, csrfToken) {
    return {
        link: initialLink || '',
        saving: false,
        copied: false,

        async saveLink(interviewId) {
            if (this.saving) return;
            this.saving = true;
            const feedback = document.getElementById('meeting-link-feedback-' + interviewId);
            try {
                const response = await fetch(
                    '/interviewer/assignments/' + encodeURIComponent(interviewId) + '/meeting-link',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                            // CsrfValidationFilter reads the header; the controller reads the form field.
                            'X-CSRF-Token': csrfToken || ''
                        },
                        body: new URLSearchParams({
                            csrfToken: csrfToken || '',
                            meetingLink: (this.link || '').trim()
                        }).toString()
                    });
                if (response.ok) {
                    if (feedback) feedback.innerHTML = await response.text(); // server-built fragment
                } else {
                    this.showError(feedback, response.status === 403
                        ? 'Session expired. Refresh the page and try again.'
                        : 'Could not save the link. Try again.');
                }
            } catch (err) {
                this.showError(feedback, 'Could not save the link. Try again.');
            } finally {
                this.saving = false;
            }
        },

        showError(feedback, message) {
            if (!feedback) return;
            const span = document.createElement('span');
            span.className = 'iw-sched-ml-feedback iw-sched-ml-feedback--error';
            span.textContent = message;
            feedback.replaceChildren(span);
        },

        async copyLink() {
            try {
                await navigator.clipboard.writeText(this.link);
            } catch (err) {
                const area = document.createElement('textarea');
                area.value = this.link;
                area.style.position = 'fixed';
                area.style.opacity = '0';
                document.body.appendChild(area);
                area.select();
                document.execCommand('copy');
                document.body.removeChild(area);
            }
            this.copied = true;
            setTimeout(() => { this.copied = false; }, 2000);
        }
    };
}

/**
 * Profile photo uploader (jobseeker /profile/edit): file validation, local preview, drag-drop.
 *
 * Lives in the always-loaded bundle on purpose. A page script appended by an hx-boost swap
 * runs after Alpine has already initialised the component, so `avatarUploader` was undefined
 * and `:src` removed the image source (bug 525).
 *
 * The upload/delete requests are driven by htmx; the host element forwards the lifecycle with
 * `@htmx:before-request="onRequestStart($event)"` / `@htmx:after-request="onRequestEnd($event)"`.
 */
function avatarUploader() {
    return {
        isDragging: false,
        isUploading: false,
        showSuccess: false,
        previewUrl: null,
        errorMessage: null,

        handleFileSelect(event) {
            const file = event.target.files?.[0];
            if (file) this.processFile(file);
        },

        handleDrop(event) {
            this.isDragging = false;
            const file = event.dataTransfer?.files?.[0];
            if (!file) return;
            this.processFile(file);
            // Hand the dropped file to the hidden input so htmx uploads it.
            const input = document.getElementById('avatar-file');
            if (input) {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                input.files = dataTransfer.files;
                htmx.trigger(input, 'change');
            }
        },

        processFile(file) {
            this.errorMessage = null;

            const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                this.errorMessage = 'Please select a JPG, PNG, or WebP image';
                return false;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                this.errorMessage = 'File too large. Maximum 5 MB allowed';
                return false;
            }

            this.isUploading = true;
            const reader = new FileReader();
            reader.onload = (e) => { this.previewUrl = e.target.result; };
            reader.readAsDataURL(file);
            return true;
        },

        onRequestStart(event) {
            if (event.detail.target?.id !== 'avatar-container') return;
            this.isUploading = true;
            this.errorMessage = null;
        },

        onRequestEnd(event) {
            if (event.detail.target?.id !== 'avatar-container') return;
            this.isUploading = false;
            if (!event.detail.successful) return;
            this.previewUrl = null;
            this.showSuccess = true;
            setTimeout(() => { this.showSuccess = false; }, 2000);
        }
    };
}

/**
 * Cross-page compare selection (Talent search + Talent pool).
 *
 * Registered as an Alpine store (not a component) so the selection survives
 * HTMX swaps of the results list and is reachable from any nested Alpine
 * scope via $store.compareTray. Persisted in sessionStorage so pagination,
 * filtering, and switching between Search and Pool keep the tray intact.
 */
function compareTrayStore() {
    const STORAGE_KEY = 'yesnojobs.compareTray';
    return {
        max: 3,
        entries: [],

        init() {
            try {
                const raw = sessionStorage.getItem(STORAGE_KEY);
                const parsed = raw ? JSON.parse(raw) : [];
                if (Array.isArray(parsed)) {
                    this.entries = parsed
                        .filter(e => e && typeof e.id === 'string' && e.id)
                        .slice(0, this.max);
                }
            } catch (e) {
                this.entries = [];
            }
        },

        persist() {
            try {
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries));
            } catch (e) {
                // Session storage unavailable (private mode quota) — tray still works in-memory.
            }
        },

        get count() {
            return this.entries.length;
        },

        get isFull() {
            return this.entries.length >= this.max;
        },

        get canCompare() {
            return this.entries.length >= 2;
        },

        has(id) {
            return this.entries.some(e => e.id === id);
        },

        toggle(id, name, initials) {
            if (!id) return;
            if (this.has(id)) {
                this.remove(id);
                return;
            }
            if (this.isFull) return;
            this.entries.push({ id: id, name: name || '', initials: initials || '' });
            this.persist();
        },

        remove(id) {
            this.entries = this.entries.filter(e => e.id !== id);
            this.persist();
        },

        clear() {
            this.entries = [];
            this.persist();
        },

        compareUrl() {
            return '/business/sourcing/compare?ids='
                + this.entries.map(e => encodeURIComponent(e.id)).join(',');
        }
    };
}

configureHtmx();

Promise.all([
    import('alpinejs'),
    import('@alpinejs/collapse')
]).then(([alpineModule, collapseModule]) => {
    const Alpine = alpineModule.default;
    const collapse = collapseModule.default;

    Alpine.plugin(collapse);
    // Alpine invokes the store's init() automatically on registration.
    Alpine.store('compareTray', compareTrayStore());
    Alpine.data('localGreeting', localGreeting);
    Alpine.data('pickTimePicker', pickTimePicker);
    Alpine.data('dropdown', dropdown);
    Alpine.data('mobileNav', mobileNav);
    Alpine.data('timezoneDetect', timezoneDetect);
    Alpine.data('schedulingForm', schedulingForm);
    Alpine.data('locationAutocomplete', locationAutocomplete);
    Alpine.data('phoneInput', phoneInput);
    Alpine.data('tagsInput', tagsInput);
    Alpine.data('fileUploader', fileUploader);
    Alpine.data('jobsPage', jobsPage);
    Alpine.data('sourcingPage', sourcingPage);
    Alpine.data('sourcingSearchBar', sourcingSearchBar);
    Alpine.data('jobsSearchBar', jobsSearchBar);
    Alpine.data('companiesSearchBar', companiesSearchBar);
    Alpine.data('sortDropdown', sortDropdown);
    Alpine.data('resumeSwitcher', resumeSwitcher);
    Alpine.data('saveSearchButton', saveSearchButton);
    Alpine.data('evaluationForm', evaluationForm);
    Alpine.data('starRating', starRating);
    Alpine.data('shareButton', shareButton);
    Alpine.data('avatarUploader', avatarUploader);
    Alpine.data('meetingLinkManager', meetingLinkManager);

    window.Alpine = Alpine;
    window.localGreeting = localGreeting;
    window.dropdown = dropdown;
    window.schedulingForm = schedulingForm;
    window.locationAutocomplete = locationAutocomplete;
    window.tagsInput = tagsInput;
    window.fileUploader = fileUploader;
    window.jobsPage = jobsPage;
    window.sourcingPage = sourcingPage;
    window.sortDropdown = sortDropdown;
    window.saveSearchButton = saveSearchButton;
    window.evaluationForm = evaluationForm;
    window.starRating = starRating;
    window.shareButton = shareButton;
    window.meetingLinkManager = meetingLinkManager;
    Alpine.start();
});
